import Check from '@mui/icons-material/Check';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  AiToolCallState,
  TreadAgentSingleRecordWriteType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import ConfirmationCardShell, {
  ApplyingIndicator,
} from '~tread-agent/ConfirmationCardShell';
import {
  COMPACT_ACTION_BUTTON_SX,
  terminalStatusColor,
} from '~tread-agent/confirmationCardStyles';
import TerminalStatusIcon from '~tread-agent/TerminalStatusIcon';
import ToolCallPreviewDetails, {
  LabeledValuePanel,
} from '~tread-agent/ToolCallPreviewDetails';
import { confirmationTitle, isTerminalToolCallState } from '~tread-agent/treadAgentUtils';
import { TreadAgentToolCallPreview, TreadAgentToolCallState } from '~tread-agent/types';

// Read-only outcome shown once a confirmation has resolved, in place of the action buttons.
function TerminalStatus({ state }: { state: TreadAgentToolCallState }) {
  const theme = useTheme();
  const color = terminalStatusColor(state, theme);

  return (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
      <TerminalStatusIcon state={state} />
      <Typography sx={{ color, fontSize: '12px', fontWeight: 500 }}>
        {t(`tread_agent.confirmation.${state}`)}
      </Typography>
    </Box>
  );
}

interface ConfirmationCardProps {
  isBusy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  output?: Record<string, unknown> | null;
  preview: TreadAgentToolCallPreview | null;
  state: TreadAgentToolCallState;
  toolName: string;
}

export default function ConfirmationCard({
  isBusy,
  onCancel,
  onConfirm,
  output,
  preview,
  state,
  toolName,
}: ConfirmationCardProps) {
  const theme = useTheme();
  const isAwaitingResponse = isBusy || state === AiToolCallState.CONFIRMING;
  const isTerminal = isTerminalToolCallState(state);
  const isSingleRecordWrite =
    preview?.kind === TreadAgentSingleRecordWriteType.SINGLE_RECORD_WRITE;
  const title = confirmationTitle(preview, toolName);
  const recordLabel = isSingleRecordWrite ? preview.target.label : null;
  // On success the payload only echoes the state chip and the diff; on failure it carries the reason.
  const shouldShowOutput = state === AiToolCallState.FAILED;

  return (
    <ConfirmationCardShell>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
        }}
      >
        <EditNoteOutlined sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 16 }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{title}</Typography>

        {recordLabel !== null && (
          <Typography
            sx={{
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              '&::before': { content: '"· "' },
            }}
          >
            {recordLabel}
          </Typography>
        )}
      </Box>

      <ToolCallPreviewDetails preview={preview} />

      {isTerminal ? (
        <>
          {shouldShowOutput && output && (
            <LabeledValuePanel
              heading={t('tread_agent.confirmation.result')}
              values={output}
            />
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TerminalStatus state={state} />
          </Box>
        </>
      ) : isAwaitingResponse ? (
        <ApplyingIndicator />
      ) : (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <SmallButton
            color="secondary"
            onClick={onCancel}
            sx={COMPACT_ACTION_BUTTON_SX}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Green"
            onClick={onConfirm}
            startIcon={<Check sx={{ fontSize: 14 }} />}
            sx={COMPACT_ACTION_BUTTON_SX}
            variant="contained"
          >
            {t('actions.confirm')}
          </SmallLoadingButton>
        </Box>
      )}
    </ConfirmationCardShell>
  );
}
